import React, { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Wallet, HandCoins, CreditCard, Banknote } from "lucide-react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useSettings } from "../context/SettingsContext";
import SquarePaymentPortal from "./SquarePaymentPortal";
/* ---------------- BASE METHODS ---------------- */

const BASE_PAYMENT_METHODS = [
  { id: "cod", label: "Cash on Delivery", icon: Wallet, type: "cash" },
  { id: "collection", label: "Pay on Collection", icon: HandCoins, type: "cashoncollection" },
];

/* ---------------- PLACE ORDER ---------------- */

const placeOrder = async ({ orderId, amount, type, response }) => {
  try {
    const { data } = await axios.post(`${window.BASE_API_URL}place-order`, {
      orderId,
      amount,
      type,
      response,
    });

    if (data?.success) {
      return { success: true, orderId: data.id, orderNo: data.order_no };
    }

    toast.error("Payment failed");
  } catch {
    toast.error("Payment error");
  }

  return { success: false };
};


const StripeForm = ({ orderId, amount, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    setLoading(false);

    if (error) {
      toast.error(error.message);
    } else {
      const res = await placeOrder({
        orderId,
        amount,
        type: "stripe",
        response: paymentIntent,
      });

      if (res.success) onSuccess(res);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PaymentElement />

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="w-1/2 bg-gray-200 py-2 rounded"
        >
          Back
        </button>

        <button
          type="submit"
          disabled={!stripe || loading}
          className="w-1/2 bg-blue-600 text-white py-2 rounded"
        >
          {loading ? "Processing…" : "Pay"}
        </button>
      </div>
    </form>
  );
};

/* ---------------- MAIN MODAL ---------------- */

const PaymentModal = ({
  isOpen,
  onClose,
  orderId,
  amount,
  onSuccess,
  paymentType = "all",
}) => {
  const { settings } = useSettings();

  const [selectedMethod, setSelectedMethod] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSquare, setShowSquare] = useState(false);
const autoTriggered = useRef(false);
  const paymentLock = useRef(false);
  const isProcessingPayment = useRef(false);

  /* -------- RESET ON OPEN -------- */
useEffect(() => {
  const style = document.createElement("style");
  style.innerHTML = premiumStyles;
  document.head.appendChild(style);

  return () => {
    document.head.removeChild(style);
  };
}, []);
  useEffect(() => {
    if (isOpen) {
      setSelectedMethod("");
      setClientSecret("");
      setLoading(false);
      setShowSquare(false);
      paymentLock.current = false;
      autoTriggered.current = false;
    }
  }, [isOpen]);

  /* -------- FILTER METHODS -------- */

  const filteredMethods = useMemo(() => {
    let m = [...BASE_PAYMENT_METHODS];

    if (settings?.is_stripe == 1)
      m.push({ id: "stripe", label: "Stripe", icon: CreditCard, type: "card" });

    if (settings?.is_squareup == 1)
      m.push({ id: "squareup", label: "Square", icon: Banknote, type: "card" });
    if (settings?.is_xendit == 1)
    m.push({ id: "xendit", label: "Xendit", icon: CreditCard, type: "card" });

      if (settings?.is_windcave == 1)
    m.push({ id: "windcave", label: "Windcave", icon: CreditCard, type: "card" });

    if (paymentType !== "all")
      m = m.filter((x) => x.type === paymentType);

    return m;
  }, [settings, paymentType]);

  /* -------- AUTO SELECT IF ONLY ONE -------- */

  useEffect(() => {
    if (!isOpen) return;

    if (filteredMethods.length === 1) {
          autoTriggered.current = true;
      handlePayment(filteredMethods[0].id);
    }
  }, [filteredMethods, isOpen]);

  /* -------- HANDLE PAYMENT -------- */

  const handlePayment = async (method) => {
    if (!method) return toast.error("Select payment");
    if (paymentLock.current) return;
  if (isProcessingPayment.current) return;

  isProcessingPayment.current = true;
    /* ----- CASH ----- */
    if (["cod", "collection"].includes(method)) {
      paymentLock.current = true;
      setLoading(true);

      const res = await placeOrder({ orderId, amount, type: method });

      setLoading(false);
      paymentLock.current = false;
       isProcessingPayment.current = false;

      if (res.success) onSuccess(res);
      return;
    }


if (method === "windcave") {
  try {
  setLoading(true);

    // const orderId = `GIFT-${Date.now()}`;

    const { data } = await axios.post(`${window.BASE_API_URL}windcave_request`, {
      orderno: orderId,
      amount: amount.toFixed(2),
      currency: localStorage.getItem("currency_name") || "USD",
    });

    if (data?.success && data?.link) {
      // 🔥 DIRECT REDIRECT  
      window.location.href = data.link;
    } else {
      toast.error("❌ Windcave initialization failed");
    }
  } catch (err) {
    console.error("Windcave init error:", err);
    toast.error("❌ Windcave init failed");
  } finally {
     setLoading(false);

    isProcessingPayment.current = false;

  }
}

if (method === "xendit") {

    try {

        setLoading(true);

        const { data } = await axios.post(

            `${window.BASE_API_URL}xendit/create-payment`,

            {
                orderId,
                amount,
                 currency: localStorage.getItem("currency_name"),
                 type: 'order'
            }

        );

        if (
            data.success &&
            data.checkout_url
        ) {

            window.location.href =
                data.checkout_url;

            return;
        }

        toast.error(
            data.message ||
            "Xendit init failed"
        );

    }

    catch(error){

        toast.error(

            error?.response?.data
            ?.message ||

            "Xendit payment failed"

        );

    }

    setLoading(false);

    isProcessingPayment.current = false;

    return;
}

    /* ----- STRIPE ----- */
    if (method === "stripe") {
      try {
        setLoading(true);

        const { data } = await axios.post(
          `${window.BASE_API_URL}stripeintents`,
          {
            orderId,
            amount,
            currency: localStorage.getItem("currency_name"),
          }
        );

        setClientSecret(data.client_secret);

        if (!stripePromise) {
          setStripePromise(await loadStripe(settings.stripe_api_key));
        }

        setSelectedMethod("stripe");
      } catch {
        toast.error("Stripe init failed");
      }

      setLoading(false);
       isProcessingPayment.current = false;
      return;
    }

    /* ----- SQUARE ----- */
    if (method === "squareup") {
      setShowSquare(true);
       isProcessingPayment.current = false;
      return;
    }


  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50">
  <div className="relative w-[450px] p-[1px] rounded-2xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 animate-gradient">

    <div className="bg-white/90 backdrop-blur-xl p-8 rounded-2xl shadow-2xl">

          {loading && (
            <div className="text-center py-10">Processing...</div>
          )}

        {!clientSecret && !loading && !selectedMethod && (
            <>
  <div className="space-y-4">
  <h2 className="text-2xl font-bold text-center mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
    Select Payment Method
  </h2>

  {filteredMethods.map((m) => {
    const Icon = m.icon;

    return (
      <button
        key={m.id}
        onClick={() => handlePayment(m.id)}
        className="group relative w-full p-5 rounded-2xl 
                   bg-white 
                   border border-gray-200
                   hover:border-transparent
                   shadow-md hover:shadow-2xl
                   transition-all duration-300
                   hover:-translate-y-1"
      >
        <div className="flex items-center gap-5">

          {/* Icon */}
          <div className="p-4 rounded-xl 
                          bg-gradient-to-br from-blue-500 to-purple-600
                          shadow-lg
                          group-hover:scale-110
                          transition duration-300">
            <Icon size={24} className="text-white" />
          </div>

          {/* Text */}
          <div className="text-left">
            <h4 className="font-semibold text-lg text-gray-800">
              {m.label}
            </h4>
            <p className="text-sm text-gray-400">
              100% secure encrypted payment
            </p>
          </div>
        </div>

        {/* Glow effect */}
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 
                        bg-gradient-to-r from-blue-500/10 to-purple-500/10 
                        transition duration-300" />
      </button>
    );
  })}
</div>

            
                <button
  onClick={onClose}
  className="w-full mt-6 py-3 rounded-xl 
             border border-gray-300 
             text-gray-600 font-medium
             hover:bg-gray-100
             hover:border-gray-400
             transition-all duration-300"
>
  Cancel
</button>
        
            </>
          )}


          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripeForm
                orderId={orderId}
                amount={amount}
                onSuccess={onSuccess}
                onBack={() => {
                   if (filteredMethods.length === 1) {
      onClose(); // close modal
    } else {
      setClientSecret(""); // normal back
    }
                }}
              />
            </Elements>
          )}
        </div>
      </div>
      </div>


      <SquarePaymentPortal
        open={showSquare}
        orderId={orderId}
        amount={amount}
        onSuccess={onSuccess}
       onClose={() => {
  if (filteredMethods.length === 1) {
    onClose();
  } else {
    setShowSquare(false);
  }
}}
        settings={settings}
      />
    </>
  );
};

/* ---------------- PREMIUM CSS ---------------- */

const premiumStyles = `
@keyframes gradientMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.animate-gradient {
  background-size: 300% 300%;
  animation: gradientMove 6s ease infinite;
}

@keyframes modalEnter {
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-animate {
  animation: modalEnter 0.35s ease forwards;
}

@keyframes shine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
}

.shine-effect::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  height: 100%;
  width: 50%;
  background: linear-gradient(
    120deg,
    transparent,
    rgba(255,255,255,0.6),
    transparent
  );
  transform: skewX(-20deg);
}

.shine-effect:hover::before {
  animation: shine 0.8s ease forwards;
}

.cancel-btn {
  position: relative;
  overflow: hidden;
}

.cancel-btn::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 12px;
  opacity: 0;
  background: linear-gradient(90deg, #6366f1, #a855f7);
  transition: opacity 0.3s ease;
}

.cancel-btn:hover::after {
  opacity: 0.1;
}

.input {
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  outline: none;
  transition: 0.3s;
}

.input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.2);
}

.btn-primary {
  width: 50%;
  padding: 10px;
  border-radius: 12px;
  background: linear-gradient(to right, #6366f1, #a855f7);
  color: white;
}

.btn-secondary {
  width: 50%;
  padding: 10px;
  border-radius: 12px;
  background: #e5e7eb;
}
`;
export default PaymentModal;